import React, { useState, useEffect, useCallback } from 'react';
import { Building, Plus, Calendar } from 'lucide-react';
import firmService from '../services/firmService';
import CreateFirmModal from '../components/firms/CreateFirmModal';
import Notification from '../components/common/Notification';

const Firms = () => {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });

  const showNotification = useCallback((title, message, variant = 'info') => {
    setNotification({
      isOpen: true,
      title,
      message,
      variant
    });
  }, []);

  const fetchFirms = useCallback(async () => {
    try {
      const data = await firmService.getAllFirms();
      setFirms(data);
    } catch (error) {
      console.error('Error fetching firms:', error);
      showNotification('Error', 'Failed to fetch firms', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchFirms();
  }, [fetchFirms]);

  const handleCreateSuccess = (result) => {
    showNotification(
      'Success',
      result.message,
      'success'
    );
    fetchFirms();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading firms...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Firms</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage law firms and onboard new clients
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Firm
        </button>
      </div>

      {/* Firms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {firms.map((firm) => (
          <div
            key={firm.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Building className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {firm.name}
                  </h3>
                  {firm.is_active ? (
                    <span className="inline-block px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded">
                      Active
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded">
                      Inactive
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Firm Details */}
            <div className="space-y-2 text-sm text-gray-600">
              {firm.email && (
                <div className="flex items-center">
                  <span className="font-medium">Email:</span>
                  <span className="ml-2">{firm.email}</span>
                </div>
              )}
              {firm.phone && (
                <div className="flex items-center">
                  <span className="font-medium">Phone:</span>
                  <span className="ml-2">{firm.phone}</span>
                </div>
              )}
              {firm.created_at && (
                <div className="flex items-center text-gray-500">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>
                    Created {new Date(firm.created_at).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {firms.length === 0 && (
        <div className="text-center py-12">
          <Building className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No firms</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new firm.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Firm
            </button>
          </div>
        </div>
      )}

      {/* Create Firm Modal */}
      <CreateFirmModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
      />
    </div>
  );
};

export default Firms;
